import React, {useState} from 'react'
import {AgencyProps} from "../../../api/model/agency";
import {allInternationalPhonePrefixesFormattedForDropdownAlpha2CountryCode} from "../../../components/settings/utils";
import {useTranslation} from "react-i18next";
import {IconButton, Modal, RadioButtonGroup, Text} from "atmosfair-ui";
import {theme} from "../../../styles/theme";
import {AllActivityTypesArray} from "../../../types-enums/activity";
import {createTravelComponentsPdf} from "../../../pdf-creation/create-travel-components-pdf";
import download from "downloadjs";
import {createJourneyPdf} from "../../../pdf-creation/create-journey-pdf";
import {useAppSelector} from "../../../redux/reducer/store";
import {BundleType} from "../common";


interface DownloadPdfButtonProps {
    activities: AllActivityTypesArray,
    whatPdfToCreate: BundleType,
    disabled?: boolean,
}

const DownloadPdfButton = ({
                               whatPdfToCreate,
                               disabled,
                               activities,
                           }: DownloadPdfButtonProps) => {
    const {
        name: travelComponentsName,
        activities: travelComponentActivities
    } = useAppSelector(state => state.travelComponents)
    const {
        name: journeyName,
        description: journeyDescription,
        journeyDuration: journeyDuration,
        customer: journeyCustomer,
        personCount: journeyPersonCount,
        activities: journeyActivities
    } = useAppSelector(state => state.journey)
    const {userData, agencies, agenciesLogos} = useAppSelector(state => state.user)
    const [selectAgencyModalVisible, setSelectAgencyModalVisible] = useState(false)
    const [selectedAgency, setSelectedAgency] = useState(0)
    const {t, i18n} = useTranslation()
    const noAgencyCreated = agencies?.length === 0

    const _getUserNameAndAgency = (index: number) => {
        if (userData && agencies) {
            const {firstName, lastName, email, phoneNumber, internationalPhonePrefix} = userData
            setSelectAgencyModalVisible(false)
            const {name, address, id} = agencies[index]
            let pathLogo: ArrayBuffer | null = null
            let agencyLogoFileType: string | null = null
            if (agenciesLogos && agenciesLogos[id]) {
                const {arrayBuffer, fileType} = agenciesLogos[id]
                if (arrayBuffer) pathLogo = arrayBuffer
                if (fileType) agencyLogoFileType = fileType
            }
            const {street, zipCode, city, houseNumber} = address
            const agencyName = name
            const agencyAddress = `${street} ${houseNumber}, ${city} ${zipCode}`
            const userName = `${firstName} ${lastName}`
            const completePhoneNumber = `${allInternationalPhonePrefixesFormattedForDropdownAlpha2CountryCode[internationalPhonePrefix]} ${phoneNumber}`
            return {userName, agencyName, agencyAddress, pathLogo, agencyLogoFileType, email, completePhoneNumber}
        }
    }

    const _onDownloadPdf = async () => {
        setSelectAgencyModalVisible(false)
        const userNameAgencyInfo = _getUserNameAndAgency(selectedAgency)
        if (userNameAgencyInfo) {
            const {
                userName,
                agencyName,
                agencyAddress,
                pathLogo,
                agencyLogoFileType,
                email,
                completePhoneNumber
            } = userNameAgencyInfo
            let pdfBytes: Uint8Array | null = null
            let bundlesName: string | number | null = ''
            let travelComponentsOrJourney = ''
            switch (whatPdfToCreate) {
                case BundleType.journey:
                    if (journeyPersonCount !== null) pdfBytes = await createJourneyPdf(
                        activities,
                        i18n,
                        agencyName,
                        agencyAddress,
                        userName,
                        email,
                        completePhoneNumber,
                        {
                            pathToLogo: pathLogo,
                            fileType: agencyLogoFileType
                        },
                        journeyDuration,
                        journeyName,
                        journeyDescription,
                        journeyCustomer,
                        journeyPersonCount
                    )
                    bundlesName = journeyName
                    travelComponentsOrJourney = t("GENERAL.JOURNEY")
                    break;
                case BundleType.travelComponents:
                    pdfBytes = await createTravelComponentsPdf(
                        activities,
                        i18n,
                        agencyName,
                        agencyAddress,
                        userName,
                        email,
                        completePhoneNumber, {
                            pathToLogo: pathLogo,
                            fileType: agencyLogoFileType
                        })
                    bundlesName = travelComponentsName
                    travelComponentsOrJourney = t("GENERAL.TRAVEL_COMPONENTS")
                    break;
                default:
                    whatPdfToCreate satisfies never
            }
            if (pdfBytes !== null) download(
                pdfBytes,
                `${bundlesName} ${userName} ${agencyName} ${travelComponentsOrJourney}.pdf`,
                "application/pdf"
            )
        }

    }

    const _onCloseAgencyModal = () => {
        setSelectAgencyModalVisible(false)
        setSelectedAgency(0)
    }

    const _setDisableButton = () => {
        if (noAgencyCreated || disabled) return true
        switch (whatPdfToCreate) {
            case BundleType.journey:
                return journeyActivities.length === 0
            case BundleType.travelComponents:
                return travelComponentActivities.length === 0
            default:
                whatPdfToCreate satisfies never
        }
    }

    return <>
        <IconButton
            disabled={_setDisableButton()}
            tooltip={noAgencyCreated ? "TOOLTIP.COMMON.CREATE_AGENCY_PDF" : "TOOLTIP.COMMON.CREATE_PDF"}
            color={"primary"}
            size={"large"}
            onlyIcon={true}
            icon={"pdf"}
            onClick={() => agencies?.length === 1 ? _onDownloadPdf() : setSelectAgencyModalVisible(true)}/>
        <Modal
            onClose={_onCloseAgencyModal}
            onBackdropClick={_onCloseAgencyModal}
            onConfirm={() => _onDownloadPdf()}
            onEnterKeyPressed={_onDownloadPdf}
            confirmButtonText={"GENERAL.OK"}
            closeButtonText={"GENERAL.ABORT"}
            headline={"MODALS.TRAVEL_COMPONENTS.TITLE"}
            showPaper={true}
            show={selectAgencyModalVisible}>
            <Text style={{marginBottom: theme.spacer.large}}>
                MODALS.TRAVEL_COMPONENTS.TEXT
            </Text>
            <RadioButtonGroup
                fieldsetLabel={"LABELS.COMMON.SELECT_AGENCY"}
                onChange={setSelectedAgency}
                radioButtons={agencies?.map((agency: AgencyProps, index: number) => ({
                    label: agency.name,
                    value: index
                }))!}
                selected={selectedAgency}
                name={"SCREENS.TRAVEL_COMPONENTS_OVERVIEW.RADIO_BUTTON_GROUP"}
                orientation={"vertical"}
                labelStyle={{fontSize: theme.font.fontSize.regular}}
                hideLabel={false}/>

        </Modal>
    </>
}

export default DownloadPdfButton